import type { ConversationScreenState, QuestionItem } from "@/components/agents/conversation/types";
import { isConversationEnded } from "@/components/agents/conversation/conversationReducer";
import type { AgentConversationHandle } from "@/hooks/useAgentConversation";

/**
 * O que o terminal precisa de quem o alimenta. É o formato da conversa dos nós de
 * SSCUI e App Fiori (`useAgentConversation`); a estrutura organizacional monta o
 * mesmo formato a partir do registro do run. O terminal não conhece o nó.
 */
export interface TerminalSource extends Omit<AgentConversationHandle, "end"> {
  /** Onde ficam os prints do run (rota de print do log). */
  media: { projectId: string; runId: string };
  /**
   * Quem é a fonte: ações pendentes (Parar, Encerrar) de uma fonte antiga não mexem na
   * nova. A conversa de SSCUI/App Fiori usa um id por adaptador; sem isso vale o run.
   */
  scopeKey?: string;
  /** Sem `end`, o botão Encerrar não aparece (o Nó 02 só pausa). */
  end?: () => Promise<void>;
  /**
   * Se o campo pode abrir agora. O campo só abre quando isto e a regra da conversa
   * (`canType`) deixam; sem isto vale só a regra. O Nó 02 só abre com a pergunta do ID:
   * a unidade esperando a reexecução não tem a quem responder.
   */
  canReply?: boolean;
  /**
   * Se o Parar vale agora. Sem isto vale a regra da conversa (turno ativo ou script
   * rodando as linhas); o Nó 02 não tem turno e declara pela unidade em andamento.
   */
  canStop?: boolean;
  /** Limite do campo de texto; sem isto vale o da conversa (`MAX_CONVERSATION_MESSAGE_LENGTH`). */
  maxMessageLength?: number;
  /**
   * Quem está vendo não é o executor: a faixa "acompanhando" toma o lugar do campo, e
   * Parar e Encerrar somem. A trava real é o 403 do servidor.
   */
  watching?: { executorName: string | null };
}

/** Acompanha quem não é o executor. Run sem dono (legada) ou sem usuário logado: ninguém acompanha. */
export function watchingOf(
  executor: ConversationScreenState["executor"],
  viewerId: string | null | undefined,
): { executorName: string | null } | undefined {
  if (!executor || !viewerId || executor.id === viewerId) return undefined;
  return { executorName: executor.name };
}

/** A pergunta do agente que ainda espera resposta, se houver. */
export function pendingQuestionOf(state: ConversationScreenState): QuestionItem | null {
  for (let index = state.items.length - 1; index >= 0; index -= 1) {
    const item = state.items[index];
    if (item.kind === "question" && !item.answered && !item.superseded) return item;
  }
  return null;
}

/**
 * O campo de texto só abre quando o agente espera a pessoa: com pergunta pendente,
 * com a conversa aguardando/pausada, ou com o último `agent_status` em `waiting`
 * (o condutor espera a fala depois de uma recusa e não emite outro evento). Com o
 * agente trabalhando fica fechado, para a fala não cair no meio de um passo; o Parar
 * continua disponível. Enquanto uma mensagem está em envio o campo também fecha:
 * a segunda fala em voo duplicaria a resposta; ele volta a abrir se o envio falhar.
 */
export function canType(state: ConversationScreenState, connectionFailed = false): boolean {
  if (connectionFailed || state.status === null || isConversationEnded(state.status)) return false;
  if (state.pending.some((message) => message.state === "sending")) return false;
  return (
    pendingQuestionOf(state) !== null || state.status !== "running" || state.activity === "waiting"
  );
}
